import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { useConfig } from '@/hooks/useConfig'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { lettersApi } from '@/lib/api'

export default function DashboardPage() {
  const { user } = useAuth()
  const { config } = useConfig()
  const { t } = useTranslation('dashboard')

  const { data: letters } = useQuery(['user-letters'], () =>
    lettersApi.list({ page: 1, page_size: 5 })
  )

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-olive-900 mb-2">
          {t('welcome', { username: user?.username })}
        </h1>
        <p className="text-olive-600">{t('subtitle')}</p>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <QuickActionCard
          title={t('quickActions.upload.title')}
          description={t('quickActions.upload.description')}
          icon="📤"
          to="/upload"
          primary
        />
        <QuickActionCard
          title={t('quickActions.archive.title')}
          description={t('quickActions.archive.description')}
          icon="📚"
          to="/letters"
        />
        <QuickActionCard
          title={t('quickActions.help.title')}
          description={t('quickActions.help.description')}
          icon="💬"
          href={config.telegram_url}
          external
        />
      </div>

      {/* Statistics */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <StatCard label={t('stats.totalLetters')} value={letters?.total || 0} icon="📄" />
        <StatCard
          label="Total Views"
          value={(letters?.items ?? []).reduce((sum, l) => sum + (l.view_count || 0), 0)}
          icon="👁️"
        />
        <StatCard
          label="Public Letters"
          value={(letters?.items ?? []).filter((l) => l.is_published).length}
          icon="🌍"
        />
      </div>

      {/* Recent Letters */}
      <div className="card">
        <h2 className="text-xl font-semibold text-olive-900 mb-4">{t('recentLetters.title')}</h2>
        {letters && letters.items.length > 0 ? (
          <div className="space-y-4">
            {letters.items.map((letter) => (
              <div
                key={letter.id}
                className="flex items-center justify-between p-4 bg-olive-50 rounded-lg"
              >
                <div>
                  <h3 className="font-medium text-olive-900">{letter.title}</h3>
                  <p className="text-sm text-olive-600">
                    {new Date(letter.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Link to={`/letters/${letter.id}`} className="text-olive-600 hover:text-olive-800">
                  View →
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <p className="text-olive-600 mb-4">{t('recentLetters.noLetters')}</p>
            <Link to="/upload" className="btn-primary">
              {t('archive.uploadFirst')}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

function QuickActionCard({
  title,
  description,
  icon,
  to,
  href,
  primary,
  external,
}: {
  title: string
  description: string
  icon: string
  to?: string
  href?: string
  primary?: boolean
  external?: boolean
}) {
  const className = `card hover:shadow-lg transition-all hover:-translate-y-1 text-center ${
    primary ? 'bg-olive-600 text-white' : ''
  }`

  const content = (
    <>
      <div className="text-4xl mb-4">{icon}</div>
      <h3 className={`text-lg font-semibold mb-2 ${primary ? 'text-white' : 'text-olive-900'}`}>
        {title}
      </h3>
      <p className={primary ? 'text-olive-100' : 'text-olive-600'}>{description}</p>
    </>
  )

  if (external && href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {content}
      </a>
    )
  }

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    )
  }

  return <div className={className}>{content}</div>
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-olive-600">{label}</p>
          <p className="text-2xl font-bold text-olive-900">{value}</p>
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  )
}
